import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderBar from "../../../components/Header/HeaderBar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ContentWrapper from "../../../components/ContentWrapper";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../store/storeTypes";
import { Job } from "@mysnowscape/snow-lib";
import axios from "../../../services/axios/backendAxios";
import flash from "../../../services/flash";
import errors from "../../../services/errors";
import PropertyList from "../../../components/Lists/PropertyList";

export default function MergeJobsPage() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  if (typeof jobId !== "string") {
    throw new Error("Missing jobId Param");
  }
  const queryClient = useQueryClient();
  const [mergeWithJobId, setMergeWithJobId] = useState<string | null>(null);
  const currentUser = useSelector((state: ReduxState) => state.currentUser);
  const srcJobQuery = useQuery<Job>({
    queryKey: [`job/${jobId}`],
  });
  const destJobQuery = useQuery<Job>({
    queryKey: [`job/${mergeWithJobId}`],
    enabled: typeof mergeWithJobId === "string",
  });
  const mergeMutation = useMutation<
    void,
    Error,
    { srcJobId: string; destJobId: string }
  >({
    mutationFn: async ({ srcJobId, destJobId }) => {
      await axios.post("job/merge", {
        srcJobId,
        destJobId,
        deleteOldJob: false,
      });
    },
    onSuccess: () => {
      flash.success("Success!  These properties are now merged.");
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
      navigate(`/admin/property`);
    },
    onError: (err) => {
      errors.report(err);
      flash.error("Error: " + (err.message ?? err));
    },
  });
  return (
    <div className="flex flex-col items-center">
      <HeaderBar
        title={"Merge Properties"}
        buttons={[
          {
            color: "red",
            label: "Merge Now",
            disabled: typeof mergeWithJobId !== "string",
            loading: false,
            onClick: async () => {
              if (
                mergeWithJobId &&
                confirm("This action cannot be undone. Continue?")
              ) {
                await mergeMutation.mutateAsync({
                  srcJobId: jobId,
                  destJobId: mergeWithJobId,
                });
              }
            },
          },
        ]}
      />
      <ContentWrapper loading={false} className="">
        <p className="text-gray-700 mb-6 text-snow-red">
          Warning: This is an advanced action and cannot be undone. Proceed with
          care.
        </p>
        <p className="text-gray-700 mb-6">
          You can merge this property into another property. This will transfer
          all the property history from this property and move them to the new
          property. This new property can be a property you own or a property
          that has been shared with you. Please note that if the property is a
          shared property, the materials consumed will still remain on your
          account only.
        </p>
        <table className="my-4">
          <thead>
            <tr>
              <th>Source Property</th>
              <th>Destination Property</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-10">
                {srcJobQuery.data?.name ?? "Loading Details..."}
              </td>
              <td className="px-10">
                {mergeWithJobId
                  ? destJobQuery.data?.name ?? "Loading Details..."
                  : ""}
              </td>
            </tr>
          </tbody>
        </table>
        <h2 className="text-xl font-bold mt-10 text-center">
          Select A Destination Property
        </h2>
        <PropertyList
          filterContainerClass="w-full flex justify-center"
          currentUser={currentUser}
          onJobClick={async (jid) => {
            if (jobId !== jid) {
              setMergeWithJobId(jid);
            }
          }}
          showFiltersDefault={false}
          actionName="Select"
        />
      </ContentWrapper>
    </div>
  );
}
